import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Star,
  MapPin,
  Phone,
  MessageSquare,
  ListChecks,
  ArrowRight,
  ChevronUp,
  ChevronDown,
  EyeOff,
  Eye,
  Trash2,
  ImageOff,
} from 'lucide-react';
import { useTilt } from '../site/motion';

const CATEGORY_STYLES = {
  Hospital: 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300',
  'Healthcare Clinic': 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300',
  'Beauty Clinic': 'bg-pink-50 text-pink-700 dark:bg-pink-500/10 dark:text-pink-300',
  'Dental Clinic': 'bg-violet-50 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300',
  Pharmacy: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-300',
  Spa: 'bg-teal-50 text-teal-700 dark:bg-teal-500/10 dark:text-teal-300',
  Salon: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300',
  Barbershop: 'bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300',
};

export const CATEGORY_LABEL_KEYS = {
  'Healthcare Clinic': ['categories.healthcareClinic', 'Healthcare Clinic'],
  'Beauty Clinic': ['categories.beautyClinic', 'Beauty Clinic'],
  'Dental Clinic': ['categories.dentalClinic', 'Dental Clinic'],
  Spa: ['categories.spa', 'Spa'],
  Salon: ['categories.salon', 'Salon (Ladies)'],
  Barbershop: ['categories.barbershop', 'Barbershop'],
};

const genderLabel = (provider, t) => {
  const g = provider.services_for || provider.gender_preference;
  if (!g) return null;
  if (g === 'male' || g === 'men_only' || g === 'men') return t('providersPage.menOnly', 'Men');
  if (g === 'female' || g === 'women_only' || g === 'women') return t('providersPage.womenOnly', 'Women');
  return t('providersPage.mixed', 'Men & Women');
};

const ProviderResultCard = ({
  provider,
  category,
  index,
  total,
  isAdmin,
  onOpen,
  onBook,
  onServices,
  onMessage,
  onMaps,
  onTag,
  onMove,
  onToggleListing,
  onDelete,
}) => {
  const { t } = useTranslation();
  const tiltRef = useTilt(3);
  const [imgFailed, setImgFailed] = useState(false);
  const image =
    provider.logo ||
    provider.profile_image ||
    (provider.images && provider.images[0]) ||
    provider.image_url ||
    provider.image;
  const rating = provider.average_rating || provider.rating;
  const gender = genderLabel(provider, t);
  const location =
    (provider.area || provider.location?.area)
      ? `${provider.area || provider.location?.area}, Kuwait`
      : provider.address || t('providersPage.locationUnknown', 'Location not specified');

  return (
    <div className="tilt-stage" data-testid="provider-result-card">
      <div
        ref={tiltRef}
        className="tilt-card group h-full flex flex-col rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 overflow-hidden shadow-sm hover:shadow-2xl hover:border-blue-300/70 dark:hover:border-blue-400/30"
      >
        <button onClick={onOpen} className="relative block w-full overflow-hidden text-start">
          {image && !imgFailed ? (
            <img
              src={image}
              alt={provider.name}
              loading="lazy"
              className="w-full h-44 object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <div className="w-full h-44 grid place-items-center bg-gradient-to-br from-blue-600/10 via-teal-500/10 to-emerald-500/10 dark:from-blue-500/15 dark:to-emerald-500/10">
              <ImageOff className="w-7 h-7 text-gray-400" />
            </div>
          )}
          <span className="absolute inset-0 bg-gradient-to-t from-gray-950/70 via-gray-950/5 to-transparent" />

          <span className="absolute top-3 start-3 flex flex-wrap gap-1.5">
            <span
              className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide backdrop-blur-sm ${
                CATEGORY_STYLES[category] || CATEGORY_STYLES['Healthcare Clinic']
              }`}
            >
              {CATEGORY_LABEL_KEYS[category] ? t(...CATEGORY_LABEL_KEYS[category]) : category}
            </span>
            {gender && (
              <span className="rounded-full bg-white/85 dark:bg-gray-900/80 px-2.5 py-1 text-[10px] font-semibold text-gray-700 dark:text-gray-200 backdrop-blur-sm">
                {gender}
              </span>
            )}
          </span>

          {rating ? (
            <span className="absolute top-3 end-3 inline-flex items-center gap-1 rounded-full bg-white/90 dark:bg-gray-900/85 px-2.5 py-1 text-[11px] font-bold text-gray-900 dark:text-white backdrop-blur-sm">
              <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
              <span className="numeric-ltr">{rating}</span>
              {provider.total_reviews > 0 && (
                <span className="font-normal text-gray-500 numeric-ltr">({provider.total_reviews})</span>
              )}
            </span>
          ) : null}

          <span className="absolute bottom-3 start-4 end-4">
            <span className="block font-display text-lg font-bold text-white leading-tight line-clamp-1">
              {provider.name}
            </span>
          </span>
        </button>

        <div className="flex flex-col flex-1 p-5">
          {provider.description && (
            <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400 line-clamp-2">
              {provider.description}
            </p>
          )}

          <div className="mt-4 flex items-center gap-2 text-sm">
            <MapPin className="w-4 h-4 shrink-0 text-teal-500" />
            <button
              onClick={onMaps}
              className="truncate text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:underline transition-colors"
            >
              {location}
            </button>
            {provider.distance_km != null && (
              <span
                className="ms-auto shrink-0 rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 numeric-ltr"
                data-testid="distance-badge"
              >
                {provider.distance_km} km
              </span>
            )}
          </div>

          {provider.phone && (
            <div className="mt-2 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Phone className="w-4 h-4 shrink-0 text-blue-500" />
              <span className="numeric-ltr">{provider.phone}</span>
            </div>
          )}

          {provider.services?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {provider.services.slice(0, 3).map((service, i) => (
                <span
                  key={i}
                  className="rounded-full bg-gray-100 dark:bg-white/5 px-2.5 py-1 text-[11px] font-medium text-gray-600 dark:text-gray-300"
                >
                  {typeof service === 'string' ? service : service.name || service}
                </span>
              ))}
              {provider.services.length > 3 && (
                <span className="rounded-full px-2 py-1 text-[11px] font-medium text-gray-400">
                  +{provider.services.length - 3}
                </span>
              )}
            </div>
          )}

          {provider.tags?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {provider.tags.slice(0, 3).map((tag, i) => (
                <button
                  key={i}
                  onClick={() => onTag(tag)}
                  title={t('providersPage.clickToSearch', 'Click to search')}
                  className="rounded-full bg-blue-50 dark:bg-blue-500/10 px-2.5 py-1 text-[11px] font-medium text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-colors"
                >
                  #{tag}
                </button>
              ))}
            </div>
          )}

          <div className="mt-auto pt-5 space-y-2.5">
            <div className="flex gap-2">
              <button
                onClick={onBook}
                data-testid="provider-book-now-button"
                className="btn-shine group/btn flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-emerald-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 hover:shadow-xl hover:shadow-emerald-500/25 transition-shadow"
              >
                {t('hero.bookNow', 'Book Now')}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1 rtl:rotate-180" />
              </button>
              <button
                onClick={onOpen}
                data-testid="provider-view-profile-button"
                className="inline-flex items-center justify-center rounded-full border border-gray-200 dark:border-white/15 px-4 py-3 text-sm font-semibold text-gray-800 dark:text-gray-100 hover:border-blue-300 dark:hover:border-blue-400/40 transition-colors"
              >
                {t('providersPage.viewProfile', 'View')}
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={onServices}
                data-testid="provider-services-button"
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-gray-50 dark:bg-white/5 px-3 py-2.5 text-xs font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <ListChecks className="w-3.5 h-3.5" />
                {t('providersPage.services', 'Services')}
              </button>
              <button
                onClick={onMessage}
                data-testid="provider-message-button"
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-gray-50 dark:bg-white/5 px-3 py-2.5 text-xs font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                {t('nav.messages', 'Message')}
              </button>
            </div>

            {isAdmin && (
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100 dark:border-white/10">
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Admin</span>
                <button
                  onClick={() => onMove(index, 'up')}
                  disabled={index === 0}
                  title="Move up"
                  className="grid place-items-center w-8 h-8 rounded-lg bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-white/10"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onMove(index, 'down')}
                  disabled={index === total - 1}
                  title="Move down"
                  className="grid place-items-center w-8 h-8 rounded-lg bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-white/10"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onToggleListing(provider)}
                  className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 hover:bg-amber-100"
                >
                  {provider.is_listed !== false ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {provider.is_listed !== false ? 'Unlist' : 'List'}
                </button>
                <button
                  onClick={() => onDelete(provider)}
                  className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-500/10 hover:bg-rose-100"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderResultCard;
